import { bus } from './EventBus.js';
import {
  DEFAULT_BINDINGS, PAD_BUTTON, bindingLabel, buttonDown,
  radialDeadzone, sanitizeBindings,
} from './ControlsConfig.js';

const GAMEPLAY_PAD = Object.freeze({
  jump: ['confirm'], crouch: ['cancel'], reload: ['secondary'],
  fireMode: ['tertiary'], sprint: ['modifier'], ads: ['ads'], fire: ['fire'],
  forge: ['forge'], pause: ['menu'], weaponPrev: ['navLeft'],
  weaponNext: ['navRight', 'weaponNext'],
});

const PAD_EDGE_ACTIONS = Object.freeze([
  'confirm', 'cancel', 'secondary', 'tertiary', 'modifier',
  'weaponNext', 'forge', 'menu', 'navUp', 'navDown', 'navLeft', 'navRight',
]);

// Pointer-lock FPS input. Accumulates raw mouse delta per frame; gameplay code
// reads `consumeLook()` once per tick so sub-frame mouse events aren't dropped.
export class Input {
  constructor(domElement) {
    this.el = domElement;
    this.keys = new Set();
    this.mouse = { dx: 0, dy: 0, left: false, right: false, wheel: 0 };
    this._lookX = 0;
    this._lookY = 0;
    this._padLookX = 0;
    this._padLookY = 0;
    this.locked = false;
    this.fallbackLook = false;
    this.sensitivity = 0.0022;
    this.gamepadSensitivity = 2.4;
    this.invertY = false;
    this.bindings = { ...DEFAULT_BINDINGS };
    this._justPressed = new Set();
    this._justReleased = new Set();
    this._padDown = new Set();
    this._padPressed = new Set();
    this._padReleased = new Set();
    this._padSuppressed = new Set();
    this._padMove = { x: 0, z: 0 };
    this._gamepad = null;
    this._bind();
  }

  _bind() {
    const kd = (e) => {
      if (e.repeat) return;
      const c = e.code;
      this.keys.add(c);
      this._justPressed.add(c);
      if (['Space', 'Tab', 'F1', 'F5'].includes(c) && this.locked) e.preventDefault();
      bus.emit('input:keydown', c);
    };
    const ku = (e) => { this.keys.delete(e.code); this._justReleased.add(e.code); bus.emit('input:keyup', e.code); };

    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    window.addEventListener('blur', () => this.clearTransient());

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.el;
      bus.emit('input:pointerlock', this.locked);
      if (!this.locked) { this.keys.clear(); this.mouse.left = this.mouse.right = false; }
    });

    this.el.addEventListener('mousedown', (e) => {
      // `fallbackLook` is the no-pointer-lock mode: the buttons still have to
      // work, or the player cannot shoot at all.
      if (!this.locked && !this.fallbackLook) return;
      if (e.button === 0) this.mouse.left = true;
      if (e.button === 2) this.mouse.right = true;
      bus.emit('input:mousedown', e.button);
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this.mouse.left = false;
      if (e.button === 2) this.mouse.right = false;
      bus.emit('input:mouseup', e.button);
    });
    this.el.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('wheel', (e) => { if (this.locked) this.mouse.wheel += Math.sign(e.deltaY); }, { passive: true });

    document.addEventListener('mousemove', (e) => {
      if (this.locked) {
        this._lookX += e.movementX || 0;
        this._lookY += e.movementY || 0;
        return;
      }
      // Fallback look. `movementX/Y` is populated outside pointer lock too, so
      // aiming still works — it just cannot recentre the cursor, which is why
      // this is a fallback and not the default.
      if (!this.fallbackLook) return;
      this._lookX += e.movementX || 0;
      this._lookY += e.movementY || 0;
    });
  }

  /**
   * Pointer lock is a REQUEST, and it is routinely refused: an iframe without
   * `allow="pointer-lock"`, an embedded or automation browser, a user who
   * dismissed the permission, a second request too soon after an exit.
   *
   * The game used to treat refusal as "go back to the menu", so on any such
   * browser clicking to play put the player in PLAYING for one frame and then
   * bounced them straight out — forever, with nothing on screen explaining why.
   * The game was simply unplayable there, and silently so.
   *
   * So: ask for the lock, and if it has not arrived shortly after a gesture,
   * turn on the fallback and carry on. Mouse-look is slightly worse without the
   * lock; not being able to start the game at all is not "slightly worse".
   */
  enableFallbackLook(on = true) {
    if (this.fallbackLook === on) return;
    this.fallbackLook = on;
    bus.emit('input:fallbackLook', on);
  }

  // Chrome returns a promise here and rejects it for entirely normal reasons
  // (no user gesture yet, a lock request too soon after an exit). Unhandled, it
  // surfaces as a red error on the boot overlay, so swallow it deliberately.
  requestLock() {
    try {
      const p = this.el.requestPointerLock?.();
      if (p && typeof p.catch === 'function') {
        p.catch(() => this.enableFallbackLook(true));
      }
    } catch { this.enableFallbackLook(true); return; }
    // Not every engine rejects the promise — some just never fire
    // pointerlockchange. Give it a moment, then assume refusal.
    clearTimeout(this._lockProbe);
    this._lockProbe = setTimeout(() => {
      if (!this.locked) this.enableFallbackLook(true);
    }, 600);
  }
  exitLock() { document.exitPointerLock?.(); }

  down(code) { return this.keys.has(code); }
  pressed(code) { return this._justPressed.has(code); }
  released(code) { return this._justReleased.has(code); }

  setBindings(bindings) { this.bindings = sanitizeBindings(bindings); }
  bindingFor(action) { return this.bindings[action] || DEFAULT_BINDINGS[action] || null; }
  bindingLabel(action) { return bindingLabel(this.bindingFor(action)); }

  _padHas(action, set = this._padDown) {
    const aliases = GAMEPLAY_PAD[action] || [action];
    return aliases.some((name) => set.has(name));
  }

  actionDown(action) {
    const code = this.bindingFor(action);
    return (code ? this.down(code) : false) || this._padHas(action);
  }

  actionPressed(action) {
    const code = this.bindingFor(action);
    return (code ? this.pressed(code) : false) || this._padHas(action, this._padPressed);
  }

  actionReleased(action) {
    const code = this.bindingFor(action);
    return (code ? this.released(code) : false) || this._padHas(action, this._padReleased);
  }

  gamepadInfo() {
    return this._gamepad ? { connected: true, id: this._gamepad.id || 'Gamepad', mapping: this._gamepad.mapping || '' }
      : { connected: false, id: '', mapping: '' };
  }

  /** Prevent a UI confirm from becoming gameplay input until it is released. */
  suppressGamepadUntilRelease(action) {
    if (typeof action === 'string' && action) this._padSuppressed.add(action);
  }

  /** Consume an edge handled by modal UI before gameplay runs in this frame. */
  consumeGamepadAction(action) { this._padPressed.delete(action); }

  clearTransient() {
    this.keys.clear();
    this.mouse.left = this.mouse.right = false;
    this._padDown.clear();
    this._padPressed.clear();
    this._padReleased.clear();
    this._padMove = { x: 0, z: 0 };
    this._padLookX = this._padLookY = 0;
  }

  /** Poll once before gameplay/UI update. Browsers expose pads only after input. */
  beginFrame(dt = 1 / 60) {
    let pad = null;
    try {
      const pads = navigator.getGamepads?.() || [];
      pad = Array.from(pads).find((item) => item && item.connected !== false) || null;
    } catch { pad = null; }

    const wasConnected = !!this._gamepad;
    this._gamepad = pad;
    if (!!pad !== wasConnected) bus.emit('input:gamepad', this.gamepadInfo());

    const next = new Set();
    if (pad) {
      const b = (name) => buttonDown(pad.buttons?.[PAD_BUTTON[name]]);
      for (const name of ['confirm', 'cancel', 'secondary', 'tertiary', 'modifier',
        'weaponNext', 'ads', 'fire', 'forge', 'menu']) {
        if (b(name)) next.add(name);
      }
      const left = radialDeadzone(pad.axes?.[0], pad.axes?.[1]);
      const right = radialDeadzone(pad.axes?.[2], pad.axes?.[3]);
      this._padMove = { x: left.x, z: -left.y };
      const step = Math.max(0, Math.min(Number.isFinite(dt) ? dt : 1 / 60, 0.05));
      this._padLookX += right.x * this.gamepadSensitivity * step;
      this._padLookY += right.y * this.gamepadSensitivity * step;
      if (b('up') || left.y < -0.62) next.add('navUp');
      if (b('down') || left.y > 0.62) next.add('navDown');
      if (b('left') || left.x < -0.62) next.add('navLeft');
      if (b('right') || left.x > 0.62) next.add('navRight');
    } else {
      this._padMove = { x: 0, z: 0 };
    }

    this._padPressed.clear();
    this._padReleased.clear();
    for (const name of next) {
      if (!this._padDown.has(name) && !this._padSuppressed.has(name)) this._padPressed.add(name);
    }
    for (const name of this._padDown) if (!next.has(name)) this._padReleased.add(name);
    for (const name of Array.from(this._padSuppressed)) {
      if (!next.has(name)) this._padSuppressed.delete(name);
    }
    this._padDown = next;
    for (const action of PAD_EDGE_ACTIONS) {
      if (this._padPressed.has(action)) bus.emit('input:gamepadAction', { action, gamepad: this.gamepadInfo() });
    }
  }

  // Returns accumulated look delta in radians and clears the accumulator.
  consumeLook() {
    const x = this._lookX * this.sensitivity + this._padLookX;
    const y = (this._lookY * this.sensitivity + this._padLookY) * (this.invertY ? -1 : 1);
    this._lookX = 0; this._lookY = 0;
    this._padLookX = 0; this._padLookY = 0;
    return { x, y };
  }

  // Call at the very end of each frame.
  endFrame() {
    this._justPressed.clear();
    this._justReleased.clear();
    this._padPressed.clear();
    this._padReleased.clear();
    this.mouse.wheel = 0;
  }

  // Movement intent in local space: x = strafe(+right), z = forward(+fwd)
  //
  // WASD only. The arrow keys are reserved for the formula bar — reordering slots
  // is the core mechanic of the game and cannot share keys with strafing.
  moveAxis() {
    let x = 0, z = 0;
    if (this.actionDown('forward')) z += 1;
    if (this.actionDown('back')) z -= 1;
    if (this.actionDown('right')) x += 1;
    if (this.actionDown('left')) x -= 1;
    x += this._padMove.x;
    z += this._padMove.z;
    const l = Math.hypot(x, z);
    if (l > 1) { x /= l; z /= l; }
    return { x, z };
  }
}
